/**
 * Listenoperationen der Etappen-Inhalte.
 *
 * Campingplätze, Highlights und Abendtipps sind schlichte Arrays ohne IDs.
 * Jede Änderung daran läuft über diese Funktionen: sie fassen das
 * übergebene Array nie an und geben immer ein neues zurück. Damit ist die
 * Logik ohne DOM prüfbar, und dieselbe Operation lässt sich unverändert auf
 * die Schlüsselliste der Oberfläche anwenden (siehe rowKeys.ts).
 *
 * Ein Index ausserhalb der Liste ist kein Fehler, sondern passiert schlicht
 * nichts: unterwegs soll ein Fehlgriff nichts kaputtmachen.
 */

import type { Camping } from '../../lib/types'

/** Ersetzt den Eintrag an der Stelle index vollständig. */
export function replaceAt<T>(list: readonly T[], index: number, value: T): T[] {
  if (!imBereich(list, index)) return [...list]

  const next = [...list]
  next[index] = value
  return next
}

/** Ändert einzelne Felder eines Eintrags, etwa nur den Preis eines Platzes. */
export function patchAt<T extends object>(
  list: readonly T[],
  index: number,
  patch: Partial<T>,
): T[] {
  const current = list[index]
  if (current === undefined) return [...list]

  return replaceAt(list, index, { ...current, ...patch })
}

/** Nimmt den Eintrag an der Stelle index heraus, der Rest rückt auf. */
export function removeAt<T>(list: readonly T[], index: number): T[] {
  if (!imBereich(list, index)) return [...list]

  return list.filter((_, i) => i !== index)
}

/**
 * Setzt einen Eintrag an die Stelle index. Wird zum Zurückholen eines
 * gelöschten Eintrags gebraucht, der dann wieder dort landet, wo er stand.
 */
export function insertAt<T>(list: readonly T[], index: number, value: T): T[] {
  const ziel = Math.min(Math.max(Math.trunc(index) || 0, 0), list.length)

  const next = [...list]
  next.splice(ziel, 0, value)
  return next
}

/** Hängt einen Eintrag hinten an. */
export function appendItem<T>(list: readonly T[], value: T): T[] {
  return [...list, value]
}

/** Tauscht den Eintrag mit seinem Nachbarn. Am Rand bleibt die Reihenfolge. */
export function moveItem<T>(list: readonly T[], index: number, direction: -1 | 1): T[] {
  const ziel = index + direction
  if (!imBereich(list, index) || !imBereich(list, ziel)) return [...list]

  const next = [...list]
  const a = next[index]!
  const b = next[ziel]!
  next[index] = b
  next[ziel] = a
  return next
}

/** Leerer Campingplatz, wie ihn "Platz hinzufügen" anlegt. */
export function emptyCamping(): Camping {
  return { name: '', price: '', note: '' }
}

function imBereich(list: readonly unknown[], index: number): boolean {
  return Number.isInteger(index) && index >= 0 && index < list.length
}
